// BasketManifest.jsx — the compound-order manifest screen (Lab Phase 3, Step 1).
// One purchase → one basket → N pancake slots, configured by the group from
// one shared QR. STATELESS by design: this screen is a live render of
// baskets/{id} (one onSnapshot). All slot mutations go through the
// submitBasketSlot callable, whose transaction is the double-serve guard —
// kill the tab, reopen, race two phones on one slot: the doc is the truth.
//
// Reached via /order?t=<basket token> (token.basketId routes here, same
// pattern as token.campaignId → CampaignLanding).

import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import useFlashDesigns from "../hooks/useFlashDesigns";

const BRAND = {
  navy: "#0d1833", panel: "#1d52a0", gold: "#fcb31f", sky: "#4aa4e1",
  pink: "#ef537e", cream: "#ffdaad",
};

export default function BasketManifest({ token }) {
  const basketId = token?.basketId;
  const [basket, setBasket] = useState(null);
  const [missing, setMissing] = useState(false);
  const [shift, setShift] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);   // slot being configured
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Config form state (reset per slot)
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(null);  // flash design
  const [freestyle, setFreestyle] = useState(false);
  const [freestyleText, setFreestyleText] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!basketId) return;
    return onSnapshot(doc(db, "baskets", basketId), (snap) => {
      if (!snap.exists()) { setMissing(true); return; }
      setBasket({ id: snap.id, ...snap.data() });
    }, () => setMissing(true));
  }, [basketId]);

  useEffect(() => {
    if (!basket?.shiftId) return;
    return onSnapshot(doc(db, "shifts", basket.shiftId), (snap) => {
      setShift(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
  }, [basket?.shiftId]);

  const { allDesigns } = useFlashDesigns(shift);
  const designs = useMemo(() => (allDesigns || []).filter(d => !d.isFreestyle), [allDesigns]);

  const slots = basket?.slots || [];
  const remaining = slots.filter(s => s.status === "open").length;
  const slot = openIdx != null ? slots[openIdx] : null;

  function startSlot(idx) {
    setOpenIdx(idx); setError(null);
    setName(""); setSelected(null); setFreestyle(false); setFreestyleText(""); setNotes("");
  }

  async function submit() {
    if (busy || openIdx == null) return;
    setBusy(true); setError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "submitBasketSlot");
      await fn({
        basketId, slotIdx: openIdx, name: name.trim(),
        ...(slot?.ticketType === "flash" && !freestyle && selected ? { designId: selected.id, designLabel: selected.label } : {}),
        ...(slot?.ticketType === "flash" && freestyle ? { isFreestyle: true, freestyleText: freestyleText.trim() } : {}),
        ...(slot?.ticketType === "custom" && notes.trim() ? { notes: notes.trim() } : {}),
      });
      setOpenIdx(null); // snapshot brings the ✓ in
    } catch (e) {
      setError(e?.message || "Something went wrong — try again.");
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = name.trim() && !busy && (
    slot?.ticketType === "custom" ? true : (freestyle ? freestyleText.trim() : !!selected)
  );

  if (!basketId || missing) {
    return <Shell><p style={{ color: BRAND.cream, textAlign: "center" }}>This order link isn't valid anymore. See a staff member at the booth. 🥞</p></Shell>;
  }
  if (!basket) {
    return <Shell><p style={{ color: "rgba(255,255,255,0.5)", textAlign: "center" }}>Loading your order…</p></Shell>;
  }

  const shiftClosed = shift && !shift.isActive;

  return (
    <Shell>
      {basket._isTestData && (
        <div style={{ background: "rgba(252,179,31,0.15)", border: `1px solid ${BRAND.gold}`, color: BRAND.gold, borderRadius: 10, padding: "0.4rem 0.8rem", fontSize: "0.78rem", fontWeight: 700, textAlign: "center", marginBottom: "1rem" }}>
          🧪 TEST ORDER — Screen Lab data
        </div>
      )}
      <h1 style={{ color: BRAND.gold, fontSize: "1.5rem", textAlign: "center", margin: "0 0 0.3rem" }}>
        {basket.label || "Your Pancake Party"} 🥞
      </h1>
      <p style={{ color: "rgba(255,255,255,0.6)", textAlign: "center", fontSize: "0.9rem", margin: "0 0 1.2rem" }}>
        {shiftClosed
          ? "This shift has wrapped — see a staff member about any unclaimed pancakes."
          : remaining > 0
            ? `${remaining} of ${slots.length} pancake${slots.length === 1 ? "" : "s"} still to set up — pass the phone around!`
            : "All set! Watch the queue — your pancakes are coming. ✨"}
      </p>

      {slots.map((s, i) => (
        <div key={i} style={{
          background: s.status === "open" ? "rgba(255,255,255,0.06)" : "rgba(112,190,68,0.10)",
          border: `2px solid ${s.status === "open" ? "rgba(252,179,31,0.45)" : "rgba(112,190,68,0.5)"}`,
          borderRadius: 14, padding: "0.9rem 1rem", marginBottom: "0.7rem",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
            <span style={{ fontSize: "1.4rem" }}>{s.ticketType === "custom" ? "📷" : "⚡"}</span>
            <div style={{ flex: 1 }}>
              <div style={{ color: "white", fontWeight: 700, fontSize: "0.95rem" }}>
                {s.status === "open"
                  ? (s.ticketType === "custom" ? "Custom portrait pancake" : "Flash design pancake")
                  : `${s.submittedName} — #${s.queueNumber ?? "—"}`}
              </div>
              <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem" }}>
                {s.status === "open" ? "Tap to set up" : "In the queue ✓"}
              </div>
            </div>
            {s.status === "open" && !shiftClosed && (
              <button onClick={() => startSlot(i)} style={btn(openIdx === i ? BRAND.pink : BRAND.gold)}>
                {openIdx === i ? "Setting up…" : "Set up"}
              </button>
            )}
            {s.status !== "open" && s.tokenId && (
              <a href={`/order?t=${s.tokenId}`} style={{ ...btn("rgba(74,164,225,0.2)"), color: BRAND.sky, textDecoration: "none", border: `1px solid ${BRAND.sky}` }}>
                Track →
              </a>
            )}
          </div>

          {openIdx === i && s.status === "open" && (
            <div style={{ marginTop: "0.9rem", borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: "0.9rem" }}>
              <input
                value={name} onChange={e => setName(e.target.value)} placeholder="Who's this pancake for?"
                style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: 10, color: "white", fontSize: "1rem", padding: "0.7rem", outline: "none", marginBottom: "0.8rem" }}
              />
              {s.ticketType === "flash" && (
                <>
                  <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.7rem" }}>
                    <button onClick={() => setFreestyle(false)} style={toggle(!freestyle, BRAND.gold)}>Pick a design</button>
                    <button onClick={() => setFreestyle(true)} style={toggle(freestyle, BRAND.pink)}>Freestyle ✨</button>
                  </div>
                  {!freestyle ? (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))", gap: "0.5rem", maxHeight: 260, overflowY: "auto", marginBottom: "0.4rem" }}>
                      {designs.map(d => (
                        <button key={d.id} onClick={() => setSelected(d)} style={{
                          background: selected?.id === d.id ? "rgba(252,179,31,0.2)" : "rgba(255,255,255,0.05)",
                          border: `2px solid ${selected?.id === d.id ? BRAND.gold : "rgba(255,255,255,0.1)"}`,
                          borderRadius: 10, padding: "0.4rem", cursor: "pointer",
                        }}>
                          <div style={{ width: "100%", aspectRatio: "1" }}>
                            {d.imageUrl && <img src={d.imageUrl} alt={d.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
                          </div>
                          <div style={{ color: selected?.id === d.id ? BRAND.gold : "rgba(255,255,255,0.6)", fontSize: "0.68rem", fontWeight: 700, marginTop: "0.2rem" }}>{d.label}</div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <textarea
                      value={freestyleText} onChange={e => setFreestyleText(e.target.value)} maxLength={200} rows={2}
                      placeholder="Describe anything — the artist riffs on it!"
                      style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(239,83,126,0.4)", borderRadius: 10, color: "white", fontSize: "0.95rem", padding: "0.7rem", outline: "none", resize: "none", marginBottom: "0.4rem" }}
                    />
                  )}
                </>
              )}
              {s.ticketType === "custom" && (
                <textarea
                  value={notes} onChange={e => setNotes(e.target.value)} maxLength={300} rows={2}
                  placeholder="Notes for the artist (you'll hand them your photo at the booth)"
                  style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(74,164,225,0.4)", borderRadius: 10, color: "white", fontSize: "0.95rem", padding: "0.7rem", outline: "none", resize: "none", marginBottom: "0.4rem" }}
                />
              )}
              {error && <p style={{ color: "#ff8a80", fontSize: "0.8rem", margin: "0.3rem 0" }}>{error}</p>}
              <button disabled={!canSubmit} onClick={submit} style={{
                width: "100%", padding: "0.85rem", borderRadius: 12, border: "none", marginTop: "0.3rem",
                background: canSubmit ? BRAND.pink : "rgba(255,255,255,0.1)",
                color: canSubmit ? "white" : "rgba(255,255,255,0.3)",
                fontSize: "1rem", fontWeight: 700, cursor: canSubmit ? "pointer" : "not-allowed",
              }}>
                {busy ? "Joining the queue…" : "Join the queue 🥞"}
              </button>
            </div>
          )}
        </div>
      ))}

      <div style={{ textAlign: "center", marginTop: "1.4rem", color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
        thejoyofpancakes.com
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: BRAND.navy, padding: "1.4rem 1rem 3rem", fontFamily: "'Fredoka', sans-serif" }}>
      <div style={{ maxWidth: 460, margin: "0 auto" }}>{children}</div>
    </div>
  );
}

const btn = (bg) => ({
  background: bg, color: BRAND.navy, border: "none", borderRadius: 10,
  padding: "0.5rem 0.9rem", fontSize: "0.82rem", fontWeight: 800, cursor: "pointer",
  fontFamily: "'Fredoka', sans-serif",
});

const toggle = (active, color) => ({
  flex: 1, padding: "0.5rem", borderRadius: 10, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer",
  background: active ? `${color}26` : "rgba(255,255,255,0.05)",
  border: `2px solid ${active ? color : "rgba(255,255,255,0.12)"}`,
  color: active ? color : "rgba(255,255,255,0.5)",
  fontFamily: "'Fredoka', sans-serif",
});
